import axios from 'axios';
import { Album, Song } from './types';
import toast from 'react-hot-toast';

// Get base API URL from environment - use relative path by default
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Registry of blob URLs created for cover images. Object URLs are not garbage
// collected automatically, so we track them and revoke old ones to avoid a
// memory leak when albums are (re)fetched or the player remounts.
const activeCoverBlobUrls = new Set<string>();

// Cache of cover blob URLs keyed by their source path, so repeated fetchAlbums
// calls (refresh/remount) reuse the same blob instead of re-downloading (P3-2).
const coverBlobCache = new Map<string, string>();

const createTrackedObjectURL = (blob: Blob): string => {
  const url = URL.createObjectURL(blob);
  activeCoverBlobUrls.add(url);
  return url;
};

// Fetch (or reuse a cached) blob URL for a cover image path.
const fetchCoverBlobUrl = async (rawCoverUrl: string): Promise<string> => {
  // Fix path to remove leading '/music/' if present
  const fixedCoverUrl = rawCoverUrl.replace(/^\/music\//, '');

  const cached = coverBlobCache.get(fixedCoverUrl);
  if (cached) return cached;

  const imgResponse = await axios.get(`${API_BASE_URL}/files/${encodeURIComponent(fixedCoverUrl)}`, {
    responseType: 'blob'
  });
  const imageUrl = createTrackedObjectURL(imgResponse.data);
  coverBlobCache.set(fixedCoverUrl, imageUrl);
  return imageUrl;
};

// Revoke all tracked cover blob URLs and clear the cache. Call on player unmount.
export const revokeAllCoverBlobUrls = (): void => {
  activeCoverBlobUrls.forEach((url) => {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore: URL may already be revoked
    }
  });
  activeCoverBlobUrls.clear();
  coverBlobCache.clear();
};

// Utility function to retry failed API calls
const fetchWithRetry = async (url: string, options = {}, retries = 3, delay = 500) => {
  let lastError;
  
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await axios(url, options);
    } catch (error) {
      // console.log(`API request failed (attempt ${attempt + 1}/${retries}):`, error);
      lastError = error;
      
      // Only delay if we're going to retry
      if (attempt < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
        // Exponential backoff
        delay *= 2;
      }
    }
  }
  
  // If we got here, all retries failed
  throw lastError;
};

// Helper to ensure URLs are protocol-relative or HTTPS
const ensureSecureUrl = (url: string): string => {
  // If it's already a relative URL, return it
  if (url.startsWith('/')) {
    return url;
  }
  
  // If it's a full URL, ensure it uses the same protocol as the page
  if (url.startsWith('http:')) {
    // Make it protocol-relative
    return url.replace('http:', '');
  }
  
  return url;
};

// Fetch all albums with songs
export const fetchAlbums = async (): Promise<Album[]> => {
  try {
    // Covers are cached by path (see fetchCoverBlobUrl) and reused across calls;
    // blob URLs are revoked on player unmount via revokeAllCoverBlobUrls().
    const response = await fetchWithRetry(`${API_BASE_URL}/albums?includeSongs=true`);
    
    if (response.data && Array.isArray(response.data)) {
      // console.log("Raw albums data from API:", response.data);
      
      // Add secure URLs to album covers only (not audio)
      const albumsWithSecureUrls = await Promise.all(response.data.map(async (album: any) => {
        // Log each album's downloadable flag
        // console.log(`Album ${album.name} downloadable flag:`, album.downloadable);
        
        if (album.coverUrl) {
          try {
            album.coverUrl = await fetchCoverBlobUrl(album.coverUrl);
          } catch (imgErr) {
            console.error(`Error fetching album cover: ${album.name}`, imgErr);
          }
        }
        
        // Add secure URLs to cover images only, not audio files
        if (album.songs && album.songs.length > 0) {
          album.songs = await Promise.all(album.songs.map(async (song: any) => {
            // Log each song's downloadable flag
            // console.log(`Song ${song.name} downloadable flag:`, song.downloadable);
            
            if (song.coverUrl) {
              try {
                song.coverUrl = await fetchCoverBlobUrl(song.coverUrl);
              } catch (imgErr) {
                console.error(`Error fetching song cover: ${song.name}`, imgErr);
              }
            }
            return song;
          }));
        }
        
        return album;
      }));
      
      return albumsWithSecureUrls;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching albums:', error);
    toast.error('Failed to load albums');
    return [];
  }
};

// Get streaming URL for a song.
// The backend /songs/:id/stream endpoint is the single source of truth: it decides
// HLS vs direct, performs/uses HLS conversion, and returns the final streamUrl + type.
export const getSongStreamUrl = async (songId: string): Promise<string | null> => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/songs/${songId}/stream`, {}, 2, 300);

    if (response.data && response.data.streamUrl) {
      return ensureSecureUrl(response.data.streamUrl);
    }

    // Endpoint reachable but no URL returned → fall back to direct file
    if (response.status >= 200 && response.status < 300) {
      return ensureSecureUrl(`${API_BASE_URL}/songs/${songId}/file`);
    }

    console.error(`No valid streaming method available for song: ${songId}`);
    return null;
  } catch (error) {
    console.error('Error getting song stream URL:', error);
    toast.error('Failed to get stream URL');
    return null;
  }
};

// Load subtitles for a song
export const loadSubtitles = async (songId: string): Promise<any[]> => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/songs/${songId}/subtitles`);
    return response.data;
  } catch (error) {
    console.error('Error loading subtitles:', error);
    return [];
  }
};

// Check if a URL is accessible
export const checkUrlAccessible = async (url: string): Promise<boolean> => {
  try {
    const response = await fetchWithRetry(url, { method: 'HEAD' });
    return response.status >= 200 && response.status < 300;
  } catch (error) {
    console.error('URL accessibility check failed:', error);
    return false;
  }
};

// Get HLS generation status
export interface HLSStatus {
  isGenerating: boolean;
  total: number;
  completed: number;
  isComplete: boolean;
  progress: number;
  startTime: string | null;
  completedTime: string | null;
}

export const getHLSStatus = async (): Promise<HLSStatus | null> => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/status/hls-status`, {}, 1, 200);
    return response.data;
  } catch (error) {
    console.error('Error getting HLS status:', error);
    return null;
  }
};